import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import { IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch } from '@mui/material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SettingsIcon from '@mui/icons-material/Settings'

import Table, { TableProps } from '@/components/ui/tables/Table'

export type LicenseData = {
  id: string
  canDelete?: boolean
  description?: string
  name: string
  licensees?: number
  quantity?: number
}

export type LicensesTableProps = Pick<TableProps<LicenseData>, 'data' | 'loading' | 'onDeleteMany' | 'selectable'> & {
  extraColumns?: ColumnDef<LicenseData, any>[]
  onDeleteClick?: (id: string) => void
  onOpenClick?: (licenseId: string) => void
}

export const columnHelper = createColumnHelper<LicenseData>()

export function LicensesTable(props: LicensesTableProps) {
  const { data, extraColumns = [], loading, onDeleteClick, onDeleteMany, onOpenClick, selectable } = props

  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('table.licenses.header.name').toString(),
      }),
      columnHelper.accessor('description', {
        header: t('table.licenses.header.description').toString(),
      }),
      ...extraColumns,
      columnHelper.display({
        id: 'actions',
        header: ({ table }) => (
          <Stack alignItems="center" justifyContent="center">
            <PopupState variant="popover" popupId="groups-table-configuration">
              {(popupState) => (
                <>
                  <IconButton aria-label="configure columns" {...bindTrigger(popupState)}>
                    <SettingsIcon />
                  </IconButton>
                  <Menu
                    {...bindMenu(popupState)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <List>
                      {table
                        .getAllLeafColumns()
                        .filter((column) => column.id !== 'actions')
                        .map((column) => (
                          <ListItem key={column.id}>
                            <ListItemText id={column.id} primary={column.columnDef.header} />
                            <Switch edge="end" onChange={column.getToggleVisibilityHandler()} checked={column.getIsVisible()} />
                          </ListItem>
                        ))}
                    </List>
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        ),
        cell: ({ row, table }) => (
          <Stack justifyContent="flex-end" direction="row" spacing={1}>
            <PopupState variant="popover" popupId={`group-${row.id}-actions`}>
              {(popupState) => (
                <>
                  <IconButton aria-label="license actions" {...bindTrigger(popupState)} data-testid={`license-actions-${row.original.id}`}>
                    <MoreHorizIcon />
                  </IconButton>
                  <Menu
                    {...bindMenu(popupState)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      data-testid={`license-details-${row.original.id}`}
                      onClick={() => {
                        table.options.meta?.open?.(row.original.id)
                      }}
                    >
                      <ListItemIcon>
                        <LaunchIcon />
                      </ListItemIcon>
                      <ListItemText>{t('common:button.details.label')}</ListItemText>
                    </MenuItem>
                    {row.original.canDelete && (
                      <MenuItem
                        onClick={() => {
                          table.options.meta?.delete?.(row.original.id)
                          popupState.close()
                        }}
                      >
                        <ListItemIcon>
                          <DeleteRoundedIcon />
                        </ListItemIcon>
                        <ListItemText>{t('common:button.delete.label')}</ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ]
  }, [extraColumns, t])

  return (
    <Table
      columns={columns}
      data={data}
      loading={loading}
      meta={{
        open: onOpenClick,
        delete: onDeleteClick,
      }}
      onDeleteMany={onDeleteMany}
      selectable={selectable}
    />
  )
}

export default LicensesTable
